import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDraftAutosave } from "@core/hooks/useDraftAutosave";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  VoteService,
  ResponseNameTakenError,
} from "@core/services/VoteService";
import { voteRowToFormResponse } from "@core/lib/formResults";
import { getCurrentUserId, getDeviceId } from "@core/lib/guestIdentityService";
import { sendVoteConfirmationEmail } from "@core/services/EmailService";
import { shouldShowQuestion } from "@core/lib/conditionalEvaluator";
import { usePollBySlug } from "@core/hooks/usePoll";
import type {
  Poll,
  FormQuestionShape,
  FormQuestionOption,
  DateVoteValue,
  FormResponseItem,
} from "@core/lib/pollStorage";
import { getPollClosureReason } from "@core/lib/pollEnforcement";
import { Clock, Lock, AlertTriangle } from "lucide-react";
import { StructuredInput } from "./StructuredInput";
import type { ValidationType } from "@core/lib/validation";
import { RatingInput } from "./RatingInput";
import { NPSInput } from "./NPSInput";
import DateQuestionVote from "./DateQuestionVote";
import { getThemeById, applyTheme, resetTheme } from "@core/lib/themes";
import { useThemeColor } from "@core/hooks/useThemeColor";
import MultiStepFormVote from "./MultiStepFormVote";
import VoteCompletionScreen from "@core/components/voting/VoteCompletionScreen";
import { Button } from "@core/components/ui/button";
import { DooDatesLogo } from "@core/components/shared/DooDatesLogo";
import { useToast } from "@core/hooks/use-toast";
import "@core/components/polls/themed-inputs.css";

type AnswerValue =
  | string
  | string[]
  | Record<string, string | string[]>
  | number
  | DateVoteValue;

interface Props {
  idOrSlug: string;
}

export default function FormPollVote({ idOrSlug }: Props) {
  const { t } = useTranslation("forms");
  const { toast } = useToast();
  const { data: poll, isLoading, error } = usePollBySlug(idOrSlug);
  const [voterName, setVoterName] = useState("");
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({}); // Pour stocker les textes "Autre"
  const [submitted, setSubmitted] = useState(false);
  const [voterEmail, setVoterEmail] = useState("");
  const [wantsEmailCopy, setWantsEmailCopy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showDataInfo, setShowDataInfo] = useState(false);
  const [closureReason, setClosureReason] = useState<
    "expired" | "capped" | "closed" | "archived" | null
  >(null);

  // Phase 2 prod-readiness : autosave du brouillon de réponse (anti-perte sur
  // crash/refresh/fermeture avant l'envoi). Cf. useDraftAutosave.
  const draftKey = `doo-form-draft:${poll?.id}:${getDeviceId()}`;
  const isPollOpen = poll?.status !== "closed";
  const draft = useDraftAutosave(
    draftKey,
    { answers, voterName, voterEmail, otherTexts },
    { enabled: !submitted && isPollOpen },
  );
  const [draftRestored, setDraftRestored] = useState(false);
  const didRestore = useRef(false);

  useEffect(() => {
    if (didRestore.current) return;
    didRestore.current = true;
    if (submitted || !isPollOpen) return;
    const saved = draft.load();
    if (!saved) return;
    const hasContent =
      (saved.answers && Object.keys(saved.answers).length > 0) ||
      !!saved.voterName ||
      !!saved.voterEmail;
    if (!hasContent) return;
    if (saved.answers) setAnswers(saved.answers);
    if (saved.voterName) setVoterName(saved.voterName);
    if (saved.voterEmail) setVoterEmail(saved.voterEmail);
    if (saved.otherTexts) setOtherTexts(saved.otherTexts);
    setDraftRestored(true);
  }, [draft, submitted, isPollOpen]);

  const discardDraft = useCallback(() => {
    draft.clear();
    setAnswers({});
    setVoterName("");
    setVoterEmail("");
    setOtherTexts({});
    setDraftRestored(false);
  }, [draft]);

  // Vérifier si l'utilisateur a le droit de voir les résultats (toujours appelé)
  const canSeeResults = useMemo(() => {
    if (!poll || !submitted) return false;

    const visibility = (poll as any).resultsVisibility || "creator-only";

    // 1. Public : tout le monde peut voir
    if (visibility === "public") return true;

    // 2. Créateur : vérifier si c'est le créateur
    const currentUserId = getCurrentUserId();
    const isCreator = poll.creator_id === currentUserId;
    if (isCreator) return true;

    // 3. Voters : vérifier si l'utilisateur a voté (après soumission, il a voté)
    if (visibility === "voters") {
      return true; // Après soumission, l'utilisateur a voté
    }

    return false;
  }, [poll, submitted]);

  useEffect(() => {
    if (!poll) return;
    let cancelled = false;
    (async () => {
      try {
        const count = await VoteService.countByPollId(poll.id, "form");
        if (cancelled) return;
        const reason = getPollClosureReason(poll as any, count);
        setClosureReason(reason);
      } catch {
        if (cancelled) return;
        setClosureReason(getPollClosureReason(poll as any, 0));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poll]);

  // Appliquer le thème visuel
  useEffect(() => {
    if ((poll as any)?.themeId) {
      const theme = getThemeById((poll as any).themeId);
      applyTheme(theme);
    }

    // Cleanup : réinitialiser le thème au démontage
    return () => {
      resetTheme();
    };
  }, [(poll as any)?.themeId]);

  // Lire la couleur primaire du thème pour les checkboxes/radios
  const primaryColor = useThemeColor("--theme-primary", "#3B82F6");

  // Appliquer la couleur via CSS variable pour les inputs custom
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--input-accent-color",
      primaryColor,
    );
  }, [primaryColor]);

  const allQuestions = useMemo(
    () => ((poll as any)?.questions ?? []) as FormQuestionShape[],
    [poll],
  );

  // Convertir les réponses pour l'évaluation conditionnelle (convertir IDs en labels)
  const simplifiedAnswers = useMemo(() => {
    const simplified: Record<string, string | string[]> = {};
    for (const [qid, val] of Object.entries(answers)) {
      const question = allQuestions.find((q) => q.id === qid);

      if (typeof val === "number") {
        // Pour rating/nps, convertir en string pour l'évaluation conditionnelle
        simplified[qid] = val.toString();
      } else if (typeof val === "object" && !Array.isArray(val)) {
        // Pour les matrices, on considère qu'une réponse existe si au moins une ligne est remplie
        const hasAnswer = Object.values(val).some((v) =>
          Array.isArray(v) ? v.length > 0 : !!v,
        );
        simplified[qid] = hasAnswer ? "answered" : "";
      } else if (Array.isArray(val)) {
        // Pour choix multiples, convertir les IDs en labels
        simplified[qid] = val.map((optId) => {
          const option = question?.options?.find((o) => o.id === optId);
          return option?.label || optId;
        });
      } else if (typeof val === "string") {
        // Pour choix unique, convertir l'ID en label
        const option = question?.options?.find((o) => o.id === val);
        simplified[qid] = option?.label || val;
      } else {
        simplified[qid] = "";
      }
    }
    return simplified;
  }, [answers, allQuestions]);

  // Filtrer les questions visibles selon les règles conditionnelles
  const questions = useMemo(() => {
    const rules = (poll as any)?.conditionalRules ?? [];
    return allQuestions.filter((q) =>
      shouldShowQuestion(q.id, rules, simplifiedAnswers),
    );
  }, [allQuestions, (poll as any)?.conditionalRules, simplifiedAnswers]);

  const updateAnswer = (qid: string, value: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const toggleMulti = (
    qid: string,
    optionId: string,
    checked: boolean,
    maxChoices?: number,
  ) => {
    const prev = (answers[qid] as string[]) || [];
    // Empêcher le dépassement en temps réel si une limite est définie
    if (checked && maxChoices && prev.length >= maxChoices) {
      return;
    }
    let next = prev;
    if (checked) {
      next = Array.from(new Set([...prev, optionId]));
    } else {
      next = prev.filter((v) => v !== optionId);
    }
    updateAnswer(qid, next);
  };

  const validate = (): string | null => {
    if (!poll) return t("vote.error.notFound");
    if (!voterName.trim()) return t("vote.validation.nameRequired");

    for (const q of questions) {
      const qid: string = q.id;
      const required: boolean = !!q.required;
      const kind: string = q.kind || q.type || "single";
      const val = answers[qid];

      if (required) {
        if (kind === "text" || kind === "long-text") {
          if (typeof val !== "string" || !val.trim()) {
            return t("vote.validation.textRequired", {
              question: q.title || t("vote.questionFallback"),
            });
          }
        } else if (kind === "single") {
          if (typeof val !== "string" || !val) {
            return t("vote.validation.choiceRequired", {
              question: q.title || t("vote.questionFallback"),
            });
          }
        } else if (kind === "multiple") {
          const arr = Array.isArray(val) ? (val as string[]) : [];
          if (arr.length === 0) {
            return t("vote.validation.atLeastOneChoice", {
              question: q.title || t("vote.questionFallback"),
            });
          }
        } else if (kind === "matrix") {
          const matrixVal = val as
            | Record<string, string | string[]>
            | undefined;
          if (!matrixVal || Object.keys(matrixVal).length === 0) {
            return t("vote.validation.textRequired", {
              question: q.title || t("vote.questionFallback"),
            });
          }
          // Vérifier que toutes les lignes ont une réponse
          const allRowsAnswered = (q.matrixRows || []).every((row) => {
            const rowAnswer = matrixVal[row.id];
            if (Array.isArray(rowAnswer)) {
              return rowAnswer.length > 0;
            }
            // Ne pas rejeter la valeur numérique 0 via un test falsy
            return (
              rowAnswer !== null && rowAnswer !== undefined && rowAnswer !== ""
            );
          });
          if (!allRowsAnswered) {
            return t("vote.validation.allRowsRequired", {
              question: q.title || t("vote.questionFallback"),
            });
          }
        } else if (kind === "date") {
          const dateVal = val as DateVoteValue | undefined;
          if (!dateVal || !Array.isArray(dateVal) || dateVal.length === 0) {
            return t("vote.validation.voteRequired", {
              question: q.title || t("vote.questionFallback"),
            });
          }
          // Vérifier qu'au moins une date a été votée
          const hasVote = dateVal.some((vote) => vote.vote && vote.date);
          if (!hasVote) {
            return t("vote.validation.atLeastOneDate", {
              question: q.title || t("vote.questionFallback"),
            });
          }
        }
      }

      if (kind === "multiple" && Array.isArray(val)) {
        const maxChoices: number | undefined = q.maxChoices;
        if (maxChoices && val.length > maxChoices) {
          return t("vote.validation.maxChoices", {
            max: maxChoices,
            question: q.title || t("vote.questionFallback"),
          });
        }
      }

      if (
        (kind === "text" || kind === "long-text") &&
        typeof val === "string"
      ) {
        const maxLength: number | undefined = q.maxLength;
        if (maxLength && val.length > maxLength) {
          return t("vote.validation.textTooLong", {
            length: val.length,
            max: maxLength,
            question: q.title || t("vote.questionFallback"),
          });
        }
      }
    }

    return null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const v = validate();
    if (v) {
      alert(v);
      return;
    }
    if (!poll) return;

    // Validation email si demandé
    if (wantsEmailCopy && !voterEmail.trim()) {
      alert(t("vote.emailRequiredForCopy"));
      return;
    }
    if (
      wantsEmailCopy &&
      voterEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(voterEmail.trim())
    ) {
      alert(t("vote.invalidEmail"));
      return;
    }

    // Map answers object to responses array (expected by SQL build_vote_response_summary)
    const responses: FormResponseItem[] = Object.keys(answers).map((qid) => ({
      questionId: qid,
      value: answers[qid],
    }));
    const email = wantsEmailCopy ? voterEmail.trim() : undefined;
    // voterName dupliqué dans response_data : la RPC get_poll_responses
    // stripe la colonne respondent_name (PII) mais laisse passer le jsonb —
    // même pattern que les date polls pour afficher le nom sur Résultats.
    const trimmedName = voterName.trim();

    // Soumission proprement dite. `allowNameTakeover` : si une réponse existe
    // déjà sous ce nom (autre device), on confirme avant d'écraser.
    const doSubmit = async (allowNameTakeover: boolean) => {
      const voteRow = await VoteService.castVote({
        pollId: poll.id,
        respondentName: trimmedName || `guest-${getDeviceId()}`,
        responseType: "form",
        stableFingerprint: getDeviceId(),
        allowNameTakeover,
        responseData: {
          responses,
          ...(email ? { email } : {}),
          ...(trimmedName ? { voterName: trimmedName } : {}),
        },
      });
      const response = voteRowToFormResponse(voteRow);
      if (email) response.respondentEmail = email;

      // Envoyer l'email si demandé
      if (wantsEmailCopy && voterEmail.trim()) {
        try {
          await sendVoteConfirmationEmail({
            poll: poll as any,
            response,
            questions: allQuestions,
          });
        } catch (emailError) {
          // Ne pas bloquer la soumission si l'email échoue
          // L'erreur est déjà gérée par le service EmailService
        }
      }

      draft.clear();
      return true; // Indicate success
    };

    setIsSubmitting(true);
    let submissionError: unknown = null;
    try {
      try {
        await doSubmit(false);
      } catch (err) {
        // Nom déjà pris par une réponse existante (autre device/empreinte) :
        // on propose de modifier la réponse existante au lieu d'un 500.
        if (err instanceof ResponseNameTakenError) {
          if (window.confirm(t("vote.nameTakenConfirm"))) {
            await doSubmit(true);
          } else {
            return; // l'utilisateur annule : on garde la réponse existante
          }
        } else {
          submissionError = err;
        }
      }
      if (submissionError) throw submissionError;
      // Set submitted AFTER all error handling succeeds
      setSubmitted(true);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : t("vote.saveError");
      setSubmitError(errorMessage);
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
      console.error("Vote submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--theme-bg-main, #F8FAFC)" }}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!poll || error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--theme-bg-main, #F8FAFC)" }}
      >
        <p className="text-gray-600">{t("vote.error.notFound")}</p>
      </div>
    );
  }

  // 🛑 Écran de sondage fermé
  if (closureReason && !submitted) {
    const getClosureInfo = () => {
      switch (closureReason) {
        case "expired":
          return {
            title: t("vote.closed.expired.title"),
            message: t("vote.closed.expired.message"),
            icon: Clock,
            color: "text-amber-500",
            bgColor: "bg-amber-50",
            borderColor: "border-amber-100",
          };
        case "capped":
          return {
            title: t("vote.closed.capped.title"),
            message: t("vote.closed.capped.message"),
            icon: Lock,
            color: "text-blue-500",
            bgColor: "bg-blue-50",
            borderColor: "border-blue-100",
          };
        default:
          return {
            title: t("vote.closed.default.title"),
            message: t("vote.closed.default.message"),
            icon: Lock,
            color: "text-gray-500",
            bgColor: "bg-gray-50",
            borderColor: "border-gray-100",
          };
      }
    };

    const info = getClosureInfo();
    const Icon = info.icon;

    return (
      <div
        className="min-h-screen pt-32 px-4"
        style={{ backgroundColor: "var(--theme-bg-main, #F8FAFC)" }}
      >
        <div
          className={`max-w-md mx-auto rounded-3xl shadow-sm border ${info.borderColor} ${info.bgColor} p-10 text-center`}
        >
          <div
            className="inline-flex items-center justify-center p-4 sm:p-5 rounded-full shadow-sm mb-6 sm:mb-8"
            style={{ backgroundColor: "var(--theme-bg-card, #FFFFFF)" }}
          >
            <Icon className={`w-10 h-10 sm:w-12 sm:h-12 ${info.color}`} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
            {info.title}
          </h1>
          <p className="text-gray-600 text-base sm:text-lg mb-8 sm:mb-10 leading-relaxed">
            {info.message}
          </p>
          <div className="flex flex-col gap-4">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-4 bg-white border border-gray-200 text-gray-700 font-semibold rounded-2xl hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
            >
              {t("nav.backHome")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Router vers le bon composant selon displayMode
  if ((poll as any)?.displayMode === "multi-step") {
    return <MultiStepFormVote poll={poll as any} />;
  }

  if (submitted) {
    return (
      <VoteCompletionScreen
        voterName={voterName}
        onBack={() => (window.location.href = "/form")}
        onViewResults={
          canSeeResults
            ? () =>
                (window.location.href = `/poll/${poll.slug || poll.id}/results`)
            : undefined
        }
        title={t("vote.completion.title")}
        subtitle={t("vote.completion.subtitle")}
        color="violet"
        onSendEmail={async (email) => {
          if (!poll) return;
          // Construire les responses pour l'envoi manuel après-soumission
          const responses: FormResponseItem[] = Object.keys(answers).map(
            (qid) => ({
              questionId: qid,
              value: answers[qid],
            }),
          );
          const response = {
            pollId: poll.id,
            respondentName: voterName.trim(),
            respondentEmail: email,
            items: responses,
          };
          await sendVoteConfirmationEmail({
            poll: poll as any,
            response,
            questions: allQuestions,
          });
        }}
      />
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: "var(--theme-bg-main, #F8FAFC)",
      }}
    >
      {/* Logo DooQuest si activé dans les settings */}
      {(poll as any)?.settings?.showLogo !== false && (
        <div className="pt-4 pb-2 flex justify-center">
          <DooDatesLogo size="sm" product="form" />
        </div>
      )}

      <form
        onSubmit={onSubmit}
        className="max-w-2xl mx-auto p-4 sm:p-6 pt-20 space-y-6"
        aria-describedby="form-live-region"
      >
        {/* Live region for announcing validation errors or status to assistive tech */}
        <div id="form-live-region" className="sr-only" aria-live="polite">
          {error ? String(error) : ""}
        </div>
        <div>
          <h1
            className="text-2xl sm:text-3xl font-bold"
            style={{
              color: "var(--theme-text-primary, #1E293B)",
            }}
          >
            {poll.title}
          </h1>
          {poll.description && (
            <p
              className="mt-1"
              style={{
                color: "var(--theme-text-secondary, #475569)",
              }}
            >
              {poll.description}
            </p>
          )}
        </div>

        {draftRestored && (
          <div
            className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-center justify-between gap-3"
            role="status"
            data-testid="draft-restored-banner"
          >
            <span>{t("vote.draftRestored")}</span>
            <button
              type="button"
              onClick={discardDraft}
              className="underline font-medium shrink-0"
              data-testid="draft-discard"
            >
              {t("vote.draftDiscard")}
            </button>
          </div>
        )}

        {/* Informations sur l'utilisation des données avant envoi */}
        <div className="text-[11px] text-slate-500">
          <button
            type="button"
            className="w-full flex items-center justify-between gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 hover:bg-slate-100 transition-colors text-left"
            onClick={() => setShowDataInfo((prev) => !prev)}
            data-testid="rgpd-formpoll-info-toggle"
          >
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] font-medium text-slate-700">
                {t("vote.dataInfo.title")}
              </span>
              {showDataInfo && (
                <span className="text-[10px] text-slate-500">
                  {t("vote.dataInfo.summary")}
                </span>
              )}
            </div>
            <span className="text-[10px] text-slate-500">
              {showDataInfo
                ? t("vote.dataInfo.hideDetails")
                : t("vote.dataInfo.showDetails")}
            </span>
          </button>

          {showDataInfo && (
            <div
              className="mt-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600"
              data-testid="rgpd-formpoll-info"
            >
              <p className="mb-1">{t("vote.dataInfo.body1")}</p>
              <p className="mb-1">{t("vote.dataInfo.body2")}</p>
              <p>
                {t("vote.dataInfo.body3Prefix")}{" "}
                <Link
                  to="/privacy"
                  className="text-blue-600 hover:text-blue-500 underline underline-offset-2"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("vote.dataInfo.privacyLink")}
                </Link>
                .
              </p>
            </div>
          )}
        </div>

        <div>
          <label
            className="block text-sm mb-1"
            htmlFor="voter-name-input"
            style={{
              color: "var(--theme-text-secondary, #475569)",
            }}
          >
            {t("vote.voterName.label")}
          </label>
          <input
            id="voter-name-input"
            type="text"
            className="w-full rounded px-3 py-2"
            style={{
              backgroundColor: "var(--theme-bg-input, #F1F5F9)",
              borderColor: "var(--theme-border, #E2E8F0)",
              borderWidth: "1px",
              color: "var(--theme-text-primary, #1E293B)",
            }}
            value={voterName}
            onChange={(e) => setVoterName(e.target.value)}
            placeholder={t("vote.voterName.placeholder")}
            aria-required="true"
            required
            aria-describedby="voter-name-desc"
          />
          <p id="voter-name-desc" className="sr-only">
            {t("vote.voterNameDesc")}
          </p>
        </div>

        <div className="space-y-6">
          {questions.map((q: FormQuestionShape, index: number) => {
            const kind: string = q.kind || q.type || "single";
            const qid: string = q.id;
            const val = answers[qid];
            return (
              <div
                key={qid}
                className="rounded-md p-4"
                style={{
                  backgroundColor: "var(--theme-bg-card, #FFFFFF)",
                  borderColor: "var(--theme-border, #E2E8F0)",
                  borderWidth: "1px",
                }}
                role="group"
                aria-labelledby={`q - ${qid} -label`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <div
                      id={`q - ${qid} -label`}
                      className="font-medium flex items-start gap-2"
                      style={{
                        color: "var(--theme-text-primary, #1E293B)",
                      }}
                    >
                      <span className="text-sm font-bold opacity-60 shrink-0">
                        Q{index + 1}.
                      </span>
                      <span className="min-w-0 break-words">
                        {q.title || t("vote.untitledQuestion")}
                      </span>
                    </div>
                    <div
                      className="text-xs"
                      style={{
                        color: "var(--theme-text-secondary, #475569)",
                      }}
                    >
                      {kind === "text" || kind === "long-text"
                        ? t("vote.kind.freeText")
                        : kind === "single"
                          ? t("questionKind.single")
                          : kind === "multiple"
                            ? t("questionKind.multiple")
                            : kind === "matrix"
                              ? t("questionKind.matrix")
                              : kind === "rating"
                                ? t("questionKind.rating")
                                : kind === "nps"
                                  ? t("questionKind.nps")
                                  : kind === "date"
                                    ? t("questionKind.date")
                                    : t("questionKind.generic")}
                      {q.required ? t("vote.required.suffix") : ""}
                    </div>
                  </div>
                  {kind === "multiple" && q.maxChoices ? (
                    <div
                      className="text-xs"
                      style={{
                        color: "var(--theme-text-secondary, #475569)",
                      }}
                    >
                      {t("vote.selectedCount", {
                        count: Array.isArray(val)
                          ? (val as string[]).length
                          : 0,
                        max: q.maxChoices,
                      })}
                    </div>
                  ) : null}
                </div>

                {kind === "text" && (
                  <>
                    {q.validationType ? (
                      <StructuredInput
                        value={typeof val === "string" ? val : ""}
                        onChange={(newVal) => updateAnswer(qid, newVal)}
                        validationType={q.validationType as ValidationType}
                        required={q.required}
                        placeholder={q.placeholder}
                      />
                    ) : (
                      <input
                        type="text"
                        className="w-full rounded px-3 py-2"
                        style={{
                          backgroundColor: "var(--theme-bg-input, #F1F5F9)",
                          borderColor: "var(--theme-border, #E2E8F0)",
                          borderWidth: "1px",
                          color: "var(--theme-text-primary, #1E293B)",
                        }}
                        placeholder={q.placeholder || t("vote.textPlaceholder")}
                        maxLength={q.maxLength || undefined}
                        value={typeof val === "string" ? val : ""}
                        onChange={(e) => updateAnswer(qid, e.target.value)}
                        aria-labelledby={`q - ${qid} -label`}
                        aria-required={q.required ? true : undefined}
                      />
                    )}
                  </>
                )}

                {kind === "long-text" && (
                  <>
                    {q.validationType ? (
                      <StructuredInput
                        value={typeof val === "string" ? val : ""}
                        onChange={(newVal) => updateAnswer(qid, newVal)}
                        validationType={q.validationType as ValidationType}
                        required={q.required}
                        placeholder={q.placeholder}
                      />
                    ) : (
                      <textarea
                        rows={6}
                        className="w-full rounded px-3 py-2 resize-y"
                        style={{
                          backgroundColor: "var(--theme-bg-input, #F1F5F9)",
                          borderColor: "var(--theme-border, #E2E8F0)",
                          borderWidth: "1px",
                          color: "var(--theme-text-primary, #1E293B)",
                        }}
                        placeholder={
                          q.placeholder || t("vote.longTextPlaceholder")
                        }
                        maxLength={q.maxLength || undefined}
                        value={typeof val === "string" ? val : ""}
                        onChange={(e) => updateAnswer(qid, e.target.value)}
                        aria-labelledby={`q - ${qid} -label`}
                        aria-required={q.required ? true : undefined}
                      />
                    )}
                  </>
                )}

                {kind === "single" && (
                  <div className="space-y-2">
                    {(q.options || []).map((opt: FormQuestionOption) => (
                      <div key={opt.id} className="space-y-1">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={qid}
                            checked={val === opt.id}
                            onChange={() => updateAnswer(qid, opt.id)}
                            aria-labelledby={`q - ${qid} -label`}
                            required={q.required || undefined}
                            data-themed="true"
                          />
                          <span
                            style={{
                              color: "var(--theme-text-primary, #1E293B)",
                            }}
                          >
                            {opt.label || t("vote.option")}
                          </span>
                          {opt.isOther && (
                            <span
                              className="text-xs"
                              style={{
                                color: "var(--theme-primary, #3B82F6)",
                              }}
                            >
                              {t("vote.freeTextBadge")}
                            </span>
                          )}
                        </label>
                        {opt.isOther && val === opt.id && (
                          <input
                            type="text"
                            className="w-full ml-6 rounded px-3 py-2 text-sm"
                            style={{
                              backgroundColor: "var(--theme-bg-input, #F1F5F9)",
                              borderColor: "var(--theme-border, #E2E8F0)",
                              borderWidth: "1px",
                              color: "var(--theme-text-primary, #1E293B)",
                            }}
                            placeholder={t("vote.specifyPlaceholder")}
                            value={otherTexts[qid] || ""}
                            onChange={(e) =>
                              setOtherTexts((prev) => ({
                                ...prev,
                                [qid]: e.target.value,
                              }))
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {kind === "multiple" && (
                  <div className="space-y-2">
                    {(q.options || []).map((opt: FormQuestionOption) => {
                      const checked = Array.isArray(val)
                        ? (val as string[]).includes(opt.id)
                        : false;
                      const selectedCount = Array.isArray(val)
                        ? (val as string[]).length
                        : 0;
                      const disableExtra =
                        !checked &&
                        q.maxChoices &&
                        selectedCount >= q.maxChoices;
                      return (
                        <div key={opt.id} className="space-y-1">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) =>
                                toggleMulti(
                                  qid,
                                  opt.id,
                                  e.currentTarget.checked,
                                  q.maxChoices,
                                )
                              }
                              aria-labelledby={`q - ${qid} -label`}
                              aria-required={q.required ? true : undefined}
                              disabled={!!disableExtra}
                              data-testid="multi-option"
                              data-themed="true"
                            />
                            <span
                              style={{
                                color: "var(--theme-text-primary, #1E293B)",
                              }}
                            >
                              {opt.label || t("vote.option")}
                            </span>
                            {opt.isOther && (
                              <span
                                className="text-xs"
                                style={{
                                  color: "var(--theme-primary, #3B82F6)",
                                }}
                              >
                                + Texte libre
                              </span>
                            )}
                          </label>
                          {opt.isOther && checked && (
                            <input
                              type="text"
                              className="w-full ml-6 rounded px-3 py-2 text-sm"
                              style={{
                                backgroundColor:
                                  "var(--theme-bg-input, #F1F5F9)",
                                borderColor: "var(--theme-border, #E2E8F0)",
                                borderWidth: "1px",
                                color: "var(--theme-text-primary, #1E293B)",
                              }}
                              placeholder={t("vote.specifyPlaceholder")}
                              value={otherTexts[`${qid}_${opt.id} `] || ""}
                              onChange={(e) =>
                                setOtherTexts((prev) => ({
                                  ...prev,
                                  [`${qid}_${opt.id} `]: e.target.value,
                                }))
                              }
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {kind === "matrix" && (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          <th
                            className="p-2 text-left text-sm"
                            style={{
                              backgroundColor: "var(--theme-bg-input, #F1F5F9)",
                              borderColor: "var(--theme-border, #E2E8F0)",
                              borderWidth: "1px",
                            }}
                          >
                            {/* Empty corner cell */}
                          </th>
                          {(q.matrixColumns || []).map(
                            (col: FormQuestionOption) => (
                              <th
                                key={col.id}
                                className="p-2 text-center text-sm font-medium"
                                style={{
                                  backgroundColor:
                                    "var(--theme-bg-input, #F1F5F9)",
                                  borderColor: "var(--theme-border, #E2E8F0)",
                                  borderWidth: "1px",
                                  color: "var(--theme-text-primary, #1E293B)",
                                }}
                              >
                                {col.label}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {(q.matrixRows || []).map((row: FormQuestionOption) => {
                          const rowValue = (
                            val as Record<string, string | string[]>
                          )?.[row.id];
                          return (
                            <tr key={row.id}>
                              <td
                                className="p-2 text-sm font-medium"
                                style={{
                                  borderColor: "var(--theme-border, #E2E8F0)",
                                  borderWidth: "1px",
                                  color: "var(--theme-text-primary, #1E293B)",
                                }}
                              >
                                {row.label}
                              </td>
                              {(q.matrixColumns || []).map(
                                (col: FormQuestionOption) => {
                                  const isChecked =
                                    q.matrixType === "multiple"
                                      ? Array.isArray(rowValue) &&
                                        rowValue.includes(col.id)
                                      : rowValue === col.id;
                                  return (
                                    <td
                                      key={col.id}
                                      className="p-2 text-center"
                                      style={{
                                        borderColor:
                                          "var(--theme-border, #E2E8F0)",
                                        borderWidth: "1px",
                                      }}
                                    >
                                      <input
                                        type={
                                          q.matrixType === "multiple"
                                            ? "checkbox"
                                            : "radio"
                                        }
                                        name={`${qid} -${row.id} `}
                                        checked={isChecked}
                                        data-themed="true"
                                        onChange={(e) => {
                                          const currentVal =
                                            (answers[qid] as Record<
                                              string,
                                              string | string[]
                                            >) || {};
                                          if (q.matrixType === "multiple") {
                                            const currentRowVal =
                                              (currentVal[
                                                row.id
                                              ] as string[]) || [];
                                            const newRowVal = e.target.checked
                                              ? [...currentRowVal, col.id]
                                              : currentRowVal.filter(
                                                  (id) => id !== col.id,
                                                );
                                            updateAnswer(qid, {
                                              ...currentVal,
                                              [row.id]: newRowVal,
                                            });
                                          } else {
                                            updateAnswer(qid, {
                                              ...currentVal,
                                              [row.id]: col.id,
                                            });
                                          }
                                        }}
                                        aria-labelledby={`q - ${qid} -label`}
                                        aria-required={
                                          q.required ? true : undefined
                                        }
                                      />
                                    </td>
                                  );
                                },
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {kind === "rating" && (
                  <RatingInput
                    value={typeof val === "number" ? val : null}
                    onChange={(newVal) => updateAnswer(qid, newVal)}
                    scale={q.ratingScale ?? 5}
                    style={q.ratingStyle ?? "numbers"}
                    minLabel={q.ratingMinLabel}
                    maxLabel={q.ratingMaxLabel}
                    required={q.required}
                  />
                )}

                {kind === "nps" && (
                  <NPSInput
                    value={typeof val === "number" ? val : null}
                    onChange={(newVal) => updateAnswer(qid, newVal)}
                    required={q.required}
                  />
                )}

                {kind === "date" && (
                  <DateQuestionVote
                    question={q}
                    value={
                      Array.isArray(val) ? (val as DateVoteValue) : undefined
                    }
                    onChange={(newVal) => updateAnswer(qid, newVal)}
                    required={q.required}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div
          className="border-t pt-4"
          style={{ borderColor: "var(--theme-border, #E2E8F0)" }}
        >
          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={wantsEmailCopy}
              onChange={(e) => setWantsEmailCopy(e.target.checked)}
              className="cursor-pointer"
              data-themed="true"
            />
            <span
              className="text-sm"
              style={{
                color: "var(--theme-text-primary, #1E293B)",
              }}
            >
              {t("vote.emailCopy.checkbox")}
            </span>
          </label>

          {wantsEmailCopy && (
            <div>
              <label
                className="block text-sm mb-1"
                htmlFor="voter-email"
                style={{
                  color: "var(--theme-text-secondary, #475569)",
                }}
              >
                {t("vote.yourEmail")}
              </label>
              <input
                id="voter-email"
                type="email"
                className="w-full rounded px-3 py-2"
                style={{
                  backgroundColor: "var(--theme-bg-input, #F1F5F9)",
                  borderColor: "var(--theme-border, #E2E8F0)",
                  borderWidth: "1px",
                  color: "var(--theme-text-primary, #1E293B)",
                }}
                value={voterEmail}
                onChange={(e) => setVoterEmail(e.target.value)}
                placeholder="votremail@example.com"
                required={wantsEmailCopy}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="text-white px-4 py-2 rounded transition-colors"
            style={{
              backgroundColor: "var(--theme-primary, #3B82F6)",
              borderColor: "var(--theme-primary, #3B82F6)",
              opacity: isSubmitting ? 0.6 : 1,
              cursor: isSubmitting ? "not-allowed" : "pointer",
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor =
                  "var(--theme-primary-hover, #2563EB)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "var(--theme-primary, #3B82F6)";
            }}
            data-testid="form-submit"
          >
            {isSubmitting ? t("vote.submitting") : t("vote.submit")}
          </Button>
        </div>
      </form>
    </div>
  );
}
